import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/auth";
import { Plus, Trash2, Save, Search, Loader2, Cpu, Smartphone } from "lucide-react";

const EMPTY = {
  platform: "android", brand: "", model: "", cpu: "", chipset: "",
  os_min: "", os_max: "", api_min: null, api_max: null,
  modes: [], operations: [], procedures: [], notes: "",
};

export default function CompatManager() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState({ platform: "", brand: "" });
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const params = {};
    if (q.platform) params.platform = q.platform;
    if (q.brand) params.brand = q.brand;
    const { data } = await axios.get(`${API}/compat/models`, { params });
    setItems(data);
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line

  const save = async () => {
    if (!form.brand || !form.model) return alert("Marque et Modèle requis");
    setBusy(true);
    try {
      const payload = {
        ...form,
        modes: typeof form.modes === "string" ? form.modes.split(",").map(s => s.trim()).filter(Boolean) : form.modes,
        operations: typeof form.operations === "string" ? form.operations.split(",").map(s => s.trim()).filter(Boolean) : form.operations,
        api_min: form.api_min ? Number(form.api_min) : null,
        api_max: form.api_max ? Number(form.api_max) : null,
      };
      if (editingId) {
        await axios.patch(`${API}/compat/models/${editingId}`, payload);
      } else {
        await axios.post(`${API}/compat/models`, payload);
      }
      setForm(EMPTY); setEditingId(null); await load();
    } finally { setBusy(false); }
  };

  const edit = (m) => {
    setEditingId(m.id);
    setForm({
      ...m,
      modes: (m.modes || []).join(", "),
      operations: (m.operations || []).join(", "),
    });
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce modèle ?")) return;
    await axios.delete(`${API}/compat/models/${id}`); load();
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="space-y-6" data-testid="compat-manager">
      {/* Editor */}
      <div className="card-lhs p-5">
        <div className="flex items-center gap-2 mb-4">
          <Cpu className="w-4 h-4 text-orange-500" />
          <h2 className="font-display font-bold uppercase tracking-wider text-sm">
            {editingId ? "Éditer un modèle" : "Ajouter un modèle compatible"}
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          <select className="input-lhs" value={form.platform} onChange={e => set("platform", e.target.value)} data-testid="compat-platform">
            <option value="android">Android</option>
            <option value="ios">iOS</option>
          </select>
          <input className="input-lhs" placeholder="Marque (Samsung...)" value={form.brand} onChange={e => set("brand", e.target.value)} data-testid="compat-brand" />
          <input className="input-lhs" placeholder="Modèle (Galaxy A03...)" value={form.model} onChange={e => set("model", e.target.value)} data-testid="compat-model" />
          <input className="input-lhs" placeholder="CPU (MTK/Snapdragon...)" value={form.cpu || ""} onChange={e => set("cpu", e.target.value)} />
          <input className="input-lhs" placeholder="Chipset" value={form.chipset || ""} onChange={e => set("chipset", e.target.value)} />
          <input className="input-lhs" placeholder="OS min (11)" value={form.os_min || ""} onChange={e => set("os_min", e.target.value)} />
          <input className="input-lhs" placeholder="OS max (17)" value={form.os_max || ""} onChange={e => set("os_max", e.target.value)} />
          <input className="input-lhs" placeholder="API min" type="number" value={form.api_min || ""} onChange={e => set("api_min", e.target.value)} />
          <input className="input-lhs" placeholder="API max" type="number" value={form.api_max || ""} onChange={e => set("api_max", e.target.value)} />
          <input className="input-lhs sm:col-span-2" placeholder="Modes (BROM, EDL, DFU...)" value={form.modes} onChange={e => set("modes", e.target.value)} />
          <input className="input-lhs sm:col-span-2 md:col-span-4" placeholder="Opérations (FRP_BYPASS, HELLO_BYPASS...)" value={form.operations} onChange={e => set("operations", e.target.value)} />
          <textarea className="input-lhs sm:col-span-2 md:col-span-4" rows={2} placeholder="Notes" value={form.notes || ""} onChange={e => set("notes", e.target.value)} />
        </div>
        <div className="flex gap-2 mt-3">
          <button data-testid="compat-save-btn" onClick={save} disabled={busy} className="btn-orange px-4 py-2 rounded-lg text-sm flex items-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {editingId ? "Enregistrer" : "Ajouter"}
          </button>
          {editingId && (
            <button onClick={() => { setForm(EMPTY); setEditingId(null); }} className="px-4 py-2 rounded-lg text-sm border border-zinc-700 text-zinc-300 hover:border-zinc-500">Annuler</button>
          )}
        </div>
      </div>

      {/* Filter + list */}
      <div className="card-lhs p-5">
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <select className="input-lhs sm:w-40" value={q.platform} onChange={e => setQ(x => ({ ...x, platform: e.target.value }))}>
            <option value="">Toutes plateformes</option>
            <option value="android">Android</option>
            <option value="ios">iOS</option>
          </select>
          <input className="input-lhs flex-1" placeholder="Filtrer marque" value={q.brand} onChange={e => setQ(x => ({ ...x, brand: e.target.value }))} />
          <button onClick={load} className="btn-orange px-4 py-2 rounded-lg text-sm flex items-center gap-2" data-testid="compat-search-btn">
            <Search className="w-4 h-4" /> Rechercher
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="compat-list">
          {items.map(m => (
            <div key={m.id} className="rounded-xl border border-zinc-800 bg-[#0C0C10] p-4 flex flex-col gap-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-xs text-zinc-500 font-mono-tech uppercase">
                    <Smartphone className="w-3 h-3" /> {m.platform}
                  </div>
                  <div className="font-semibold truncate">{m.brand} — {m.model}</div>
                  <div className="text-[11px] text-zinc-500 font-mono-tech">
                    {m.chipset || m.cpu} · OS {m.os_min}-{m.os_max} · API {m.api_min}-{m.api_max}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button onClick={() => edit(m)} className="text-xs px-2 py-1 border border-orange-500/40 text-orange-400 rounded" data-testid={`compat-edit-${m.id}`}>Éditer</button>
                  <button onClick={() => del(m.id)} className="text-zinc-500 hover:text-red-400" data-testid={`compat-del-${m.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                {(m.modes || []).map(x => <span key={x} className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 font-mono-tech">{x}</span>)}
                {(m.operations || []).slice(0, 4).map(x => <span key={x} className="text-[10px] px-2 py-0.5 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-400 font-mono-tech">{x}</span>)}
                {(m.operations || []).length > 4 && <span className="text-[10px] text-zinc-500">+{(m.operations || []).length - 4}</span>}
              </div>
            </div>
          ))}
          {items.length === 0 && <div className="text-sm text-zinc-500 italic">Aucun modèle. Ajoute-en un ci-dessus.</div>}
        </div>
      </div>
    </div>
  );
}
